
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';

const LANG_MAP: Record<string, { htmlLang: string; ogLocale: string }> = {
  en: { htmlLang: 'en', ogLocale: 'en_US' },
  ja: { htmlLang: 'ja', ogLocale: 'ja_JP' },
  zh: { htmlLang: 'zh-Hans', ogLocale: 'zh_CN' },
  ko: { htmlLang: 'ko', ogLocale: 'ko_KR' },
  th: { htmlLang: 'th', ogLocale: 'th_TH' },
  vi: { htmlLang: 'vi', ogLocale: 'vi_VN' },
  id: { htmlLang: 'id', ogLocale: 'id_ID' },
  ms: { htmlLang: 'ms', ogLocale: 'ms_MY' },
  fr: { htmlLang: 'fr', ogLocale: 'fr_FR' },
  de: { htmlLang: 'de', ogLocale: 'de_DE' },
};

const HREFLANG_LANGS: { hreflang: string; i18nKey: string }[] = [
  { hreflang: 'x-default', i18nKey: 'en' },
  { hreflang: 'en', i18nKey: 'en' },
  { hreflang: 'ja', i18nKey: 'ja' },
  { hreflang: 'zh-Hans', i18nKey: 'zh' },
  { hreflang: 'ko', i18nKey: 'ko' },
  { hreflang: 'th', i18nKey: 'th' },
  { hreflang: 'vi', i18nKey: 'vi' },
  { hreflang: 'id', i18nKey: 'id' },
  { hreflang: 'ms', i18nKey: 'ms' },
  { hreflang: 'fr', i18nKey: 'fr' },
  { hreflang: 'de', i18nKey: 'de' },
];

const BASE_URL = 'https://magomekominka.com';

// /support → /minpaku-license-support などのリダイレクトを正規パスに解決
const CANONICAL_PATH_MAP: Record<string, string> = {
  '/support': '/minpaku-license-support',
  '/for-owners': '/tokyo-airbnb-support',
  '/pricing': '/airbnb-management-tokyo',
};

function getCanonicalPath(pathname: string): string {
  return CANONICAL_PATH_MAP[pathname] ?? pathname;
}

export default function SeoLangSync() {
  const { i18n } = useTranslation();
  const location = useLocation();

  useEffect(() => {
    const lang = i18n.language;
    const config = LANG_MAP[lang] ?? LANG_MAP['en'];

    // <html lang="..."> を更新
    document.documentElement.lang = config.htmlLang;

    // og:locale を更新
    let ogLocale = document.querySelector('meta[property="og:locale"]');
    if (!ogLocale) {
      ogLocale = document.createElement('meta');
      (ogLocale as HTMLMetaElement).setAttribute('property', 'og:locale');
      document.head.appendChild(ogLocale);
    }
    (ogLocale as HTMLMetaElement).setAttribute('content', config.ogLocale);
  }, [i18n.language]);

  useEffect(() => {
    const canonicalPath = getCanonicalPath(location.pathname);
    const pageUrl = `${BASE_URL}${canonicalPath}`;

    // 既存の動的hreflangタグを削除（data-dynamic-hreflang属性で識別）
    document.querySelectorAll('link[data-dynamic-hreflang]').forEach((el) => el.remove());

    // 現在のページのhreflangタグを動的に追加
    HREFLANG_LANGS.forEach(({ hreflang }) => {
      const link = document.createElement('link');
      link.setAttribute('rel', 'alternate');
      link.setAttribute('hreflang', hreflang);
      link.setAttribute('href', pageUrl);
      link.setAttribute('data-dynamic-hreflang', 'true');
      document.head.appendChild(link);
    });

    // canonical タグも現在のページに合わせて更新
    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement('link');
      (canonical as HTMLLinkElement).setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
    }
    (canonical as HTMLLinkElement).setAttribute('href', pageUrl);
  }, [location.pathname]);

  return null;
}
